import { captureFamilyRequest } from '../lib/familyRequestScope';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createTenantFamilyAthlete,
  getTenantFamilyHousehold,
  getTenantFamilySchedule,
  requestTenantFamilyBooking,
  getTenantFamilyNotes,
  createTenantFamilyNote,
  updateTenantFamilyNote,
  deleteTenantFamilyNote,
  skipTenantFamilyIntake,
  getTenantFamilyBookableSlots,
  createTenantFamilyBookingHold,
  confirmTenantFamilyOffPlatformBooking,
  createTenantFamilyBookingCheckout,
} from '../lib/tenantFamily';
import type {
  TenantFamilyAthleteCreatePayload,
  TenantFamilyBookingRequestPayload,
  TenantFamilyHouseholdResponse,
  TenantFamilyScheduleResponse,
  TenantFamilyNotePayload,
  TenantFamilyNoteTimeline,
  TenantFamilyBookableSlotsResponse,
} from '../types';
import { tenantDirectoryKeys } from './useTenantDirectory';
import { scheduleKeys } from './useScheduleDashboard';

export const tenantFamilyKeys = {
  all: ['tenant-family'] as const,
  household: () => [...tenantFamilyKeys.all, 'household'] as const,
  schedule: () => [...tenantFamilyKeys.all, 'schedule'] as const,
  notes: (athleteId: number | null) => [...tenantFamilyKeys.all, 'notes', athleteId] as const,
  bookableSlots: (athleteId: number | null) => [...tenantFamilyKeys.all, 'bookable-slots', athleteId] as const,
};

export function useTenantFamilyBookableSlots(athleteId: number | null, enabled: boolean = true) {
  const queryClient = useQueryClient();
  return useQuery<TenantFamilyBookableSlotsResponse>({
    queryKey: tenantFamilyKeys.bookableSlots(athleteId),
    queryFn: () => getTenantFamilyBookableSlots(athleteId as number, 14, captureFamilyRequest(queryClient).config),
    staleTime: 30 * 1000,
    retry: false,
    enabled: enabled && athleteId != null,
  });
}

export function useTenantFamilySelfBooking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { athleteId: number; offeringId: number; startsAt: string; paymentPolicy: string; assertCurrent?: () => void }) => {
      const scope = captureFamilyRequest(queryClient, payload.assertCurrent);
      scope.assertCurrent();
      const hold = await createTenantFamilyBookingHold({
        athleteId: payload.athleteId,
        offeringId: payload.offeringId,
        startsAt: payload.startsAt,
        clientMutationId: `web-family-${payload.athleteId}-${payload.offeringId}-${Date.now()}`,
      }, scope.config);
      scope.assertCurrent();
      if (payload.paymentPolicy === 'STRIPE') {
        const checkout = await createTenantFamilyBookingCheckout(hold.holdId, {
          successUrl: `${window.location.origin}/family?checkout=success`,
          cancelUrl: `${window.location.origin}/family?checkout=cancelled`,
        }, scope.config);
        scope.assertCurrent();
        return { scope, hold, checkoutUrl: checkout.checkoutUrl };
      }
      return hold.status === 'BOOKED'
        ? { scope, hold }
        : { scope, hold: await confirmTenantFamilyOffPlatformBooking(hold.holdId, scope.config) };
    },
    onSuccess: (result, payload) => {
      if (!result.scope.isCurrent()) return;
      void queryClient.invalidateQueries({ queryKey: tenantFamilyKeys.bookableSlots(payload.athleteId) });
      void queryClient.invalidateQueries({ queryKey: tenantFamilyKeys.schedule() });
    },
  });
}

export function useTenantFamily(enabled: boolean = true) {
  const queryClient = useQueryClient();

  const query = useQuery<TenantFamilyHouseholdResponse>({
    queryKey: tenantFamilyKeys.household(),
    queryFn: () => getTenantFamilyHousehold(captureFamilyRequest(queryClient).config),
    staleTime: 60 * 1000,
    retry: false,
    enabled,
  });

  const createAthleteMutation = useMutation({
    mutationFn: async (payload: TenantFamilyAthleteCreatePayload) => {
      const scope = captureFamilyRequest(queryClient);
      const athlete = await createTenantFamilyAthlete(payload, scope.config);
      scope.assertCurrent();
      return { athlete, scope };
    },
    onSuccess: ({ scope }) => {
      if (!scope.isCurrent()) return;
      void queryClient.invalidateQueries({ queryKey: tenantFamilyKeys.household() });
      void queryClient.invalidateQueries({ queryKey: tenantFamilyKeys.schedule() });
      void queryClient.invalidateQueries({ queryKey: tenantDirectoryKeys.overview() });
    },
  });

  return {
    household: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    createAthlete: async (payload: TenantFamilyAthleteCreatePayload) => (await createAthleteMutation.mutateAsync(payload)).athlete,
    isCreatingAthlete: createAthleteMutation.isPending,
  };
}

export function useTenantFamilyNotes(athleteId: number | null, enabled: boolean = true) {
  const queryClient = useQueryClient();
  const query = useQuery<TenantFamilyNoteTimeline>({
    queryKey: tenantFamilyKeys.notes(athleteId),
    queryFn: () => getTenantFamilyNotes(athleteId as number),
    enabled: enabled && athleteId != null,
    staleTime: 30 * 1000,
    retry: false,
  });
  const setTimeline = (timeline: TenantFamilyNoteTimeline) => {
    queryClient.setQueryData(tenantFamilyKeys.notes(athleteId), timeline);
    void queryClient.invalidateQueries({ queryKey: tenantDirectoryKeys.overview() });
  };
  const saveMutation = useMutation({
    mutationFn: ({ noteId, payload }: { noteId: number | null; payload: TenantFamilyNotePayload }) =>
      noteId == null
        ? createTenantFamilyNote(athleteId as number, payload)
        : updateTenantFamilyNote(athleteId as number, noteId, payload),
    onSuccess: setTimeline,
  });
  const deleteMutation = useMutation({
    mutationFn: ({ noteId, version }: { noteId: number; version: number }) =>
      deleteTenantFamilyNote(athleteId as number, noteId, version),
    onSuccess: setTimeline,
  });
  const skipMutation = useMutation({
    mutationFn: () => skipTenantFamilyIntake(athleteId as number),
    onSuccess: setTimeline,
  });
  return {
    timeline: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    saveNote: saveMutation.mutateAsync,
    deleteNote: deleteMutation.mutateAsync,
    skipIntake: skipMutation.mutateAsync,
    isMutating: saveMutation.isPending || deleteMutation.isPending || skipMutation.isPending,
  };
}

export function useTenantFamilySchedule(enabled: boolean = true) {
  const queryClient = useQueryClient();

  const query = useQuery<TenantFamilyScheduleResponse>({
    queryKey: tenantFamilyKeys.schedule(),
    queryFn: () => getTenantFamilySchedule(captureFamilyRequest(queryClient).config),
    staleTime: 30 * 1000,
    retry: false,
    enabled,
  });

  const requestBookingMutation = useMutation({
    mutationFn: async ({ occurrenceId, payload }: { occurrenceId: number; payload: TenantFamilyBookingRequestPayload }) => {
      const scope = captureFamilyRequest(queryClient);
      const response = await requestTenantFamilyBooking(occurrenceId, payload, scope.config);
      scope.assertCurrent();
      return { response, scope };
    },
    onSuccess: ({ response, scope }) => {
      if (!scope.isCurrent()) return;
      queryClient.setQueryData(tenantFamilyKeys.schedule(), response);
      void queryClient.invalidateQueries({ queryKey: tenantFamilyKeys.household() });
      void queryClient.invalidateQueries({ queryKey: scheduleKeys.dashboard() });
      void queryClient.invalidateQueries({ queryKey: tenantDirectoryKeys.overview() });
    },
  });

  return {
    schedule: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    requestBooking: async (payload: Parameters<typeof requestBookingMutation.mutateAsync>[0]) => (await requestBookingMutation.mutateAsync(payload)).response,
    isRequestingBooking: requestBookingMutation.isPending,
  };
}
